import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, User, Mail, ArrowLeft, CheckCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { useCompanyLogo } from "@/hooks/use-company-logo";

interface ClientLoginProps {
  onLoginSuccess: () => void;
}

export default function ClientLogin({ onLoginSuccess }: ClientLoginProps) {
  const [mode, setMode] = useState<"login" | "forgot" | "sent">("login");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [forgotUsername, setForgotUsername] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const logoUrl = useCompanyLogo();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await apiRequest("POST", "/api/client-auth/login", {
        username,
        password,
      });

      if (response.ok) {
        onLoginSuccess();
      } else {
        const data = await response.json();
        toast({
          title: "Inloggning misslyckades",
          description: data.message || "Fel användarnamn eller lösenord",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Inloggning misslyckades",
        description: "Ett fel uppstod. Försök igen.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch("/api/client-auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: forgotUsername }),
      });

      const data = await response.json();

      if (!response.ok) {
        toast({
          title: "Kunde inte skicka lösenord",
          description: data.message || "Något gick fel. Försök igen.",
          variant: "destructive",
        });
      } else {
        setMode("sent");
      }
    } catch (error) {
      toast({
        title: "Något gick fel",
        description: "Kontrollera din internetanslutning och försök igen.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const logoElement = (
    <div className="flex justify-center mb-4">
      <img
        src={logoUrl}
        alt="Rätt Order"
        className="h-16 w-auto max-w-[200px] object-contain"
        data-testid="img-client-login-logo"
      />
    </div>
  );

  if (mode === "sent") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            {logoElement}
            <div className="flex justify-center mb-2">
              <CheckCircle className="h-12 w-12 text-green-500" />
            </div>
            <CardTitle className="text-2xl">E-post skickad</CardTitle>
            <CardDescription>
              Om det finns ett konto med det användarnamnet och en registrerad e-postadress har vi skickat ett nytt lösenord dit.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground text-center">
              Kontrollera din inkorg (och skräppostmappen) efter ett e-postmeddelande med ditt nya lösenord.
            </p>
            <Button
              className="w-full"
              onClick={() => { setMode("login"); setForgotUsername(""); }}
              data-testid="button-back-to-login"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Tillbaka till inloggning
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (mode === "forgot") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            {logoElement}
            <CardTitle className="text-2xl">Glömt lösenord</CardTitle>
            <CardDescription>
              Ange ditt användarnamn så skickar vi ett nytt lösenord till den e-postadress som är kopplad till kontot.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="forgot-username">Användarnamn</Label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="forgot-username"
                    type="text"
                    value={forgotUsername}
                    onChange={(e) => setForgotUsername(e.target.value)}
                    placeholder="Ditt användarnamn"
                    className="pl-9"
                    required
                    autoFocus
                    data-testid="input-forgot-username"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={isLoading || !forgotUsername.trim()}
                data-testid="button-send-new-password"
              >
                <Mail className="h-4 w-4 mr-2" />
                {isLoading ? "Skickar..." : "Skicka nytt lösenord"}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => { setMode("login"); setForgotUsername(""); }}
                data-testid="button-cancel-forgot"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Tillbaka
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          {logoElement}
          <CardTitle className="text-2xl">Kundportal</CardTitle>
          <CardDescription>
            Logga in för att beställa
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Användarnamn</Label>
              <div className="relative">
                <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Ditt användarnamn"
                  className="pl-9"
                  required
                  data-testid="input-client-username"
                />
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Lösenord</Label>
                <button
                  type="button"
                  onClick={() => setMode("forgot")}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors underline-offset-2 hover:underline"
                  data-testid="link-forgot-password"
                >
                  Glömt lösenord?
                </button>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Ditt lösenord"
                  className="pl-9"
                  required
                  data-testid="input-client-password"
                />
              </div>
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
              data-testid="button-client-login"
            >
              {isLoading ? "Loggar in..." : "Logga in"}
            </Button>
          </form>

          <div className="mt-6 pt-4 border-t text-center text-sm text-muted-foreground">
            <p>Kontakta din administratör om du inte har inloggningsuppgifter.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
